import AgoraRTC, {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  ConnectionState,
} from "agora-rtc-sdk-ng";
import { getRtcToken } from "./AgoraApiServices";

// Resolve the Agora App ID from the environment. No hardcoded fallback:
// misconfiguration should fail loudly rather than silently using a stale ID.
export const getAgoraAppId = (): string => {
  const appId = process.env.REACT_APP_AGORA_APP_ID;
  if (!appId || appId === "undefined" || appId.trim() === "") {
    throw new Error(
      "Agora App ID is not configured. Set REACT_APP_AGORA_APP_ID in your environment."
    );
  }
  return appId;
};

// Browser compatibility check
const isBrowserSupported = () => {
  return AgoraRTC.checkSystemRequirements();
};

// Detect browser type for optimizations
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isChrome =
    /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);

  return { isChrome, isFirefox, isSafari, isEdge };
};

export class AgoraService {
  private agoraClient: IAgoraRTCClient | null = null;
  public localAudioTrack: IMicrophoneAudioTrack | null = null;
  public localVideoTrack: ICameraVideoTrack | null = null;
  public screenTrack: any = null;
  private token: string;
  private isReconnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private browserInfo = getBrowserInfo();
  // Retained so tokens can be transparently renewed before they expire.
  private currentChannelName: string = "";
  private currentUid: number = 0;

  constructor(role: "admin" | "user", token: string) {
    // Check browser compatibility first
    if (!isBrowserSupported()) {
      console.error("Current browser is not supported by Agora SDK");
      throw new Error(
        "Browser not supported. Please use Chrome, Firefox, Safari, or Edge."
      );
    }

    this.token = token;
    console.log("AgoraService constructor - Token set:", this.token?.substring(0, 50) + "...");

    // Log configuration for debugging
    console.log("Agora Service Configuration:", {
      appId: getAgoraAppId(),
      tokenLength: token?.length || 0,
      environment: process.env.NODE_ENV,
      role: role,
    });

    // Enable Agora SDK logging for debugging
    AgoraRTC.enableLogUpload();
    AgoraRTC.setLogLevel(process.env.NODE_ENV === "development" ? 0 : 2);
  }

  public async initializeAgoraClient(role: "admin" | "user") {
    try {
      console.log("Initializing Agora client", role);

      // Optimize codec based on browser
      const codec = this.browserInfo.isSafari ? "h264" : "vp8";

      const agoraClient = AgoraRTC.createClient({
        mode: "live",
        codec: codec,
      });

      this.agoraClient = agoraClient;

      // Keep the RTC token fresh for long classes. Agora fires
      // `token-privilege-will-expire` ~30s before expiry (renew in place) and
      // `token-privilege-did-expire` if it lapses (fetch and renew).
      this.agoraClient.on("token-privilege-will-expire", async () => {
        await this.renewRtcToken();
      });
      this.agoraClient.on("token-privilege-did-expire", async () => {
        await this.renewRtcToken();
      });

      // Set the client role after initialization
      if (role === "admin") {
        await this.agoraClient.setClientRole("host");
      } else {
        // Use host role for students too so they can receive video streams
        await this.agoraClient.setClientRole("host");
      }
      
      console.log(`Agora client initialized with ${codec} codec and ${role} role`);
      console.log("Agora client object:", this.agoraClient);
    } catch (error) {
      console.error("Error initializing Agora client:", error);
      throw error;
    }
  }

  // Fetch a fresh RTC token and hand it to the SDK, keeping the media session
  // alive past the original token lifetime. Safe to call from both the
  // "will-expire" and "did-expire" events.
  private async renewRtcToken(): Promise<void> {
    try {
      if (!this.agoraClient || !this.currentChannelName || !this.currentUid) {
        return;
      }
      const freshToken = await getRtcToken(
        this.currentChannelName,
        this.currentUid
      );
      this.token = freshToken;
      await this.agoraClient.renewToken(freshToken);
      console.log("Agora RTC token renewed");
    } catch (error) {
      console.error("Failed to renew Agora RTC token:", error);
    }
  }

  // Validate token format and expiration
  private validateToken(token: string, appId: string): boolean {
    try {
      if (!token || token.length < 10) {
        console.error("❌ Token is too short or empty");
        return false;
      }

      // Check if token starts with the expected Agora token prefix
      if (!token.startsWith("006")) {
        console.error("❌ Token format is incorrect - should start with 006");
        console.error("Token preview:", token.substring(0, 20) + "...");
        return false;
      }

      // Check if token contains the App ID (not necessarily at the start)
      if (!token.includes(appId)) {
        console.error("❌ Token does not contain the expected App ID");
        console.error("Expected App ID:", appId);
        console.error("Token preview:", token.substring(0, 50) + "...");
        return false;
      }

      console.log("✅ Token format validation passed");
      console.log("Token preview:", token.substring(0, 50) + "...");
      return true;
    } catch (error) {
      console.error("❌ Token validation error:", error);
      return false;
    }
  }

  public async joinChannel(channelName: string, uid: number) {
    try {
      // Validate Agora client is initialized
      if (!this.agoraClient) {
        throw new Error("Agora client is not initialized. Please call initializeAgoraClient() first.");
      }

      // Debug environment variables
      console.log("=== Agora Environment Debug ===");
      console.log("REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
      console.log("REACT_APP_WEBSOCKET_URL:", process.env.REACT_APP_WEBSOCKET_URL);
      console.log("REACT_APP_AGORA_APP_ID:", process.env.REACT_APP_AGORA_APP_ID);
      console.log("REACT_APP_AGORA_APP_ID type:", typeof process.env.REACT_APP_AGORA_APP_ID);
      console.log("REACT_APP_AGORA_APP_ID length:", process.env.REACT_APP_AGORA_APP_ID?.length);
      console.log("Agora client initialized:", !!this.agoraClient);
      console.log("=================================");
      
      // Validate Agora App ID before attempting to join
      const appId = getAgoraAppId();
      if (!appId || appId === "undefined" || appId === "") {
        throw new Error(
          `Agora App ID is not configured. Current value: "${appId}". Please check your environment variables.`
        );
      }

      // Validate channel name
      if (!channelName || channelName.trim() === "") {
        throw new Error("Channel name cannot be empty");
      }

      // Clean channel name (remove any special characters that might cause issues)
      const cleanChannelName = channelName.replace(/[^a-zA-Z0-9_-]/g, '');
      console.log("Original channel name:", channelName);
      console.log("Clean channel name:", cleanChannelName);

      // Debug token information
      console.log("=== Token Debug Information ===");
      console.log("Token length:", this.token?.length || 0);
      console.log("Token preview:", this.token?.substring(0, 50) + "...");
      console.log("Token end:", this.token?.substring(this.token.length - 20));
      console.log("App ID:", appId);
      console.log("Channel name:", cleanChannelName);
      console.log("UID:", uid, "Type:", typeof uid);
      console.log("Current time:", new Date().toISOString());
      console.log("===============================");

      // Validate token before attempting to join (with fallback for development)
      if (process.env.NODE_ENV === "development") {
        console.log("🔧 Development mode: Token validation is lenient");
        if (!this.token || this.token.length < 10) {
          console.error("❌ Token is missing or too short");
          throw new Error("Invalid Agora token. Please refresh the page to get a new token.");
        }
      } else {
        // Production mode: strict validation
        if (!this.validateToken(this.token, appId)) {
          console.error("❌ Token validation failed");
          throw new Error("Invalid Agora token. Please refresh the page to get a new token.");
        }
      }

      // Check if backend is running (for token generation)
      if (
        process.env.NODE_ENV === "development" &&
        (!this.token || this.token.length < 10)
      ) {
        console.warn(
          "⚠️ Warning: Backend server might not be running. Token appears invalid."
        );
        console.warn(
          "Please start your backend server on localhost:3030 for proper token generation."
        );
      }

      console.log(
        "Joining channel:::",
        appId,
        channelName,
        "Token length:",
        this.token?.length || 0,
        "Token preview:",
        this.token?.substring(0, 50) + "...",
        "Token end:",
        this.token?.substring(this.token.length - 20),
        "Full token:",
        this.token,
        "UID:",
        uid,
        "UID type:",
        typeof uid
      );

    // Ensure UID is a number
    const numericUid = typeof uid === 'string' ? parseInt(uid, 10) : uid;
    console.log("Using numeric UID:", numericUid);
    console.log("Original UID:", uid, "Type:", typeof uid);
    
    // Validate UID is a valid number
    if (isNaN(numericUid) || numericUid <= 0) {
      throw new Error(`Invalid UID: ${uid}. UID must be a positive number.`);
    }

      await this.agoraClient?.join(appId, cleanChannelName, this.token, numericUid);

      // Remember identity so the token can be renewed without a rejoin.
      this.currentChannelName = cleanChannelName;
      this.currentUid = numericUid;

      console.log("Successfully joined channel");
      this.reconnectAttempts = 0; // Reset on successful join
    } catch (error: any) {
      console.error("Failed to join channel:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        name: error.name,
        stack: error.stack,
      });

      // Handle specific error types with more detailed messages
      if (error.code === "CAN_NOT_GET_GATEWAY_SERVER") {
        console.error("🔥 Agora gateway server connection failed");
        console.error("📋 Troubleshooting checklist:");
        console.error("1. Check if backend server is running on localhost:3032");
        console.error("2. Verify Agora App ID is correct (no quotes)");
        console.error("3. Check internet connection");
        console.error("4. Verify firewall settings");
        console.error("5. Check if Agora App Certificate is valid");
        console.error("6. Verify token is not expired");
        console.error("7. Check browser compatibility");

        // Additional debugging information
        console.error("🔍 Debug Information:");
        console.error("- App ID:", getAgoraAppId());
        console.error("- Channel Name:", channelName);
        console.error("- UID:", uid);
        console.error("- Token Length:", this.token?.length || 0);
        console.error("- Token Preview:", this.token?.substring(0, 50) + "...");
        console.error("- User Agent:", navigator.userAgent);
        console.error("- Current Time:", new Date().toISOString());

        throw new Error(
          "🚨 Unable to connect to Agora servers.\n\n" +
            "🔍 This could be due to:\n" +
            "1. ❌ Backend server not running (localhost:3030)\n" +
            "2. ❌ Invalid Agora App ID configuration\n" +
            "3. ❌ Network connectivity issues\n" +
            "4. ❌ Firewall blocking Agora servers\n" +
            "5. ❌ Invalid or expired Agora App Certificate\n" +
            "6. ❌ Browser compatibility issues\n\n" +
            "✅ Please check your configuration and network connection.\n" +
            "📱 Try refreshing the page or using a different browser."
        );
      } else if (error.code === "INVALID_PARAMS") {
        throw new Error(
          "❌ Invalid channel parameters. Please check your Agora configuration."
        );
      } else if (error.code === "UID_CONFLICT") {
        console.warn("UID conflict detected, generating new UID");
        // Generate a new UID by adding a random number
        const newUid = uid + Math.floor(Math.random() * 1000);
        console.log(`Retrying with new UID: ${newUid}`);
        // Retry with new UID - get appId and cleanChannelName from the method scope
        const appId = getAgoraAppId();
        const cleanChannelName = channelName.replace(/[^a-zA-Z0-9_-]/g, '');
        await this.agoraClient?.join(appId, cleanChannelName, this.token, newUid);
        console.log("Successfully joined channel with new UID");
        this.reconnectAttempts = 0;
        return;
      } else if (error.code === "DYNAMIC_KEY_TIMEOUT" || error.message.includes("invalid token")) {
        console.warn("Token expired or invalid, attempting to get fresh token");
        // This would require the component to handle token refresh
        throw new Error(
          "🔄 Token expired or invalid. Please refresh the page to get a new token."
        );
      } else if (error.code === "INVALID_VENDOR_KEY") {
        throw new Error(
          "❌ Invalid Agora App ID. Please check your environment configuration."
        );
      }

      // For development, provide more specific error information
      if (process.env.NODE_ENV === "development") {
        throw new Error(
          `🔥 Agora connection failed: ${error.code} - ${error.message}\n\nCheck console for detailed debugging information.`
        );
      }

      throw error;
    }
  }

  public async leaveChannel() {
    try {
      // Unpublish tracks before leaving
      if (this.localAudioTrack || this.localVideoTrack) {
        const tracksToUnpublish = [];
        if (this.localAudioTrack) tracksToUnpublish.push(this.localAudioTrack);
        if (this.localVideoTrack) tracksToUnpublish.push(this.localVideoTrack);
        if (this.screenTrack) tracksToUnpublish.push(this.screenTrack);

        if (tracksToUnpublish.length > 0) {
          await this.agoraClient?.unpublish(tracksToUnpublish);
        }
      }

      // Close tracks
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
        this.localAudioTrack = null;
      }
      if (this.localVideoTrack) {
        this.localVideoTrack.close();
        this.localVideoTrack = null;
      }
      if (this.screenTrack) {
        this.screenTrack.close();
        this.screenTrack = null;
      }

      await this.agoraClient?.leave();
      console.log("Successfully left channel");
    } catch (error) {
      console.error("Error leaving channel:", error);
    }
  }

  // Join as a host with enhanced error handling
  public async joinAsHost(
    channelName: string,
    uid: number,
    callback: () => void
  ) {
    try {
      console.log("Joining channel as host", channelName, uid);
      await this.joinChannel(channelName, uid);

      // A host can both publish tracks and subscribe to tracks
      await this.agoraClient?.setClientRole("host");
      console.log("Set client role to host");

      await this.createAndPublishLocalTracks();
      console.log("Host joined and published tracks.");

      callback();
    } catch (error) {
      console.error("Failed to join as host:", error);
      throw error;
    }
  }

  // Join as an audience
  public async joinAsAudience(channelName: string, uid: number) {
    try {
      console.log("Joining channel as audience");
      await this.joinChannel(channelName, uid);

      // Set client role to host so students can receive video streams
      await this.agoraClient?.setClientRole("host");
      console.log("Student joined as host and can subscribe to tracks.");
    } catch (error) {
      console.error("Failed to join as audience:", error);
      throw error;
    }
  }

  // Create and publish local tracks with optimized settings
  public async createAndPublishLocalTracks() {
    try {
      // Create audio track with enhanced settings
      this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        AEC: true, // Acoustic Echo Cancellation
        ANS: true, // Automatic Noise Suppression
        AGC: true, // Automatic Gain Control
        // Enhanced for different browsers
        ...(this.browserInfo.isFirefox && {
          encoderConfig: {
            sampleRate: 48000,
            stereo: true,
            bitrate: 128,
          },
        }),
      });

      // Create video track with optimized settings
      const videoConfig: any = {
        encoderConfig: {
          width: { min: 480, ideal: 1280, max: 1920 },
          height: { min: 320, ideal: 720, max: 1080 },
          frameRate: { min: 15, ideal: 30, max: 30 },
          bitrateMin: 400,
          bitrateMax: 1000,
        },
        optimizationMode: "motion", // Better for screen sharing and movement
        // Browser-specific optimizations
        ...(this.browserInfo.isSafari && {
          facingMode: "user",
        }),
      };

      this.localVideoTrack = await AgoraRTC.createCameraVideoTrack(videoConfig);

      // Publish tracks
      const tracksToPublish = [this.localAudioTrack, this.localVideoTrack];
      await this.agoraClient?.publish(tracksToPublish);

      console.log(
        "Local tracks created and published with optimized settings."
      );
      console.log("Published audio track:", !!this.localAudioTrack);
      console.log("Published video track:", !!this.localVideoTrack);
    } catch (error: any) {
      console.error("Failed to create and publish tracks:", error);

      // Handle specific media errors
      if (error.name === "NotAllowedError") {
        throw new Error(
          "Camera/microphone access denied. Please allow permissions and try again."
        );
      } else if (error.name === "NotFoundError") {
        throw new Error(
          "No camera or microphone found. Please connect a device and try again."
        );
      } else if (error.name === "NotReadableError") {
        throw new Error(
          "Camera/microphone is already in use by another application."
        );
      }

      throw error;
    }
  }

  //return client
  public getClient() {
    return this.agoraClient;
  }

  // Enhanced event listeners with comprehensive error handling
  public setupEventListeners() {
    if (!this.agoraClient) return;

    this.agoraClient.on("user-joined", (user) => {
      console.log(`User ${user.uid} joined the channel`);
    });

    this.agoraClient.on("user-left", (user) => {
      console.log(`User ${user.uid} left the channel`);
    });

    this.agoraClient.on("user-published", (user, mediaType) => {
      console.log(`User ${user.uid} published ${mediaType} track`);
    });

    this.agoraClient.on("user-unpublished", (user, mediaType) => {
      console.log(`User ${user.uid} unpublished ${mediaType} track`);
    });

    this.agoraClient.on("error", (err: any) => {
      console.error(`Agora SDK Error: ${err.message}`, err);
      this.handleAgoraError(err);
    });

    this.agoraClient.on(
      "connection-state-change",
      (state: ConnectionState, reason: any) => {
        console.log(
          `Connection state changed to ${state} because of ${reason}`
        );

        if (state === "DISCONNECTED" || state === "DISCONNECTING") {
          this.handleConnectionLoss(reason);
        } else if (state === "CONNECTED") {
          this.isReconnecting = false;
          this.reconnectAttempts = 0;
        }
      }
    );

    // Network quality monitoring
    this.agoraClient.on("network-quality", (stats) => {
      if (
        stats.downlinkNetworkQuality <= 1 ||
        stats.uplinkNetworkQuality <= 1
      ) {
        console.warn("Poor network quality detected:", stats);
        this.handlePoorNetworkQuality(stats);
      }
    });

    // Exception handling
    this.agoraClient.on("exception", (evt) => {
      console.error("Agora exception:", evt);
    });
  }

  // Handle Agora-specific errors
  private handleAgoraError(error: any) {
    switch (error.code) {
      case "CAN_NOT_GET_GATEWAY_SERVER":
        console.error(
          "Cannot connect to Agora servers. Check network connection."
        );
        break;
      case "UID_CONFLICT":
        console.error(
          "User ID conflict detected. User may be connected from another device."
        );
        break;
      case "INVALID_PARAMS":
        console.error("Invalid parameters provided to Agora SDK.");
        break;
      default:
        console.error("Unhandled Agora error:", error);
    }
  }

  // Handle connection loss with reconnection logic
  private async handleConnectionLoss(reason: string) {
    if (
      this.isReconnecting ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    console.log(
      `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
    );

    try {
      // Wait before reconnecting
      await new Promise((resolve) =>
        setTimeout(resolve, 2000 * this.reconnectAttempts)
      );

      // Try to rejoin (this would need to be called from the component with proper parameters)
      // The component should handle this by listening to connection state changes
    } catch (error) {
      console.error("Reconnection failed:", error);
      this.isReconnecting = false;
    }
  }

  // Handle poor network quality
  private handlePoorNetworkQuality(stats: any) {
    // Could implement adaptive bitrate or quality reduction
    console.log("Adapting to poor network conditions...");

    // Reduce video quality for better performance
    if (this.localVideoTrack && stats.uplinkNetworkQuality <= 1) {
      // Dynamically reduce encoder config
      // This is a placeholder - actual implementation would depend on requirements
    }
  }

  // Enhanced camera control
  async turnOffCamera() {
    try {
      if (this.localVideoTrack) {
        // Unpublish before stopping
        await this.agoraClient?.unpublish([this.localVideoTrack]);
        await this.localVideoTrack.stop();
        this.localVideoTrack.close();
        this.localVideoTrack = null;
        console.log("Camera turned off successfully");
      }
    } catch (error) {
      console.error("Error turning off camera:", error);
    }
  }

  // Enhanced microphone control
  async turnOffMicrophone() {
    try {
      if (this.localAudioTrack) {
        // Unpublish before stopping
        await this.agoraClient?.unpublish([this.localAudioTrack]);
        await this.localAudioTrack.stop();
        this.localAudioTrack.close();
        this.localAudioTrack = null;
        console.log("Microphone turned off successfully");
      }
    } catch (error) {
      console.error("Error turning off microphone:", error);
    }
  }

  // Create local tracks with enhanced settings
  public async createLocalTracks() {
    try {
      const [microphoneTrack, cameraTrack] =
        await AgoraRTC.createMicrophoneAndCameraTracks(
          // Audio config
          {
            AEC: true, // Acoustic Echo Cancellation
            ANS: true, // Automatic Noise Suppression
            AGC: true, // Automatic Gain Control
          },
          // Video config
          {
            encoderConfig: {
              width: { min: 480, ideal: 1280, max: 1920 },
              height: { min: 320, ideal: 720, max: 1080 },
              frameRate: { min: 15, ideal: 30, max: 30 },
            },
            optimizationMode: "motion",
          }
        );

      this.localAudioTrack = microphoneTrack;
      this.localVideoTrack = cameraTrack;

      console.log("Local tracks created with enhanced settings");
    } catch (error) {
      console.error("Error creating local tracks:", error);
      throw error;
    }
  }

  // Enhanced cleanup
  public stopLocalTracks() {
    try {
      if (this.localAudioTrack) {
        this.localAudioTrack.stop();
        this.localAudioTrack.close();
        this.localAudioTrack = null;
      }
      if (this.localVideoTrack) {
        this.localVideoTrack.stop();
        this.localVideoTrack.close();
        this.localVideoTrack = null;
      }
      if (this.screenTrack) {
        this.screenTrack.stop();
        this.screenTrack.close();
        this.screenTrack = null;
      }
      console.log("All local tracks stopped and cleaned up");
    } catch (error) {
      console.error("Error stopping local tracks:", error);
    }
  }

  // Control microphone mute state
  public async setMicrophoneMuted(muted: boolean) {
    try {
      if (this.localAudioTrack) {
        await this.localAudioTrack.setMuted(muted);
        console.log(`Microphone ${muted ? 'muted' : 'unmuted'}`);
      } else {
        console.warn("No local audio track available to control");
      }
    } catch (error) {
      console.error("Error controlling microphone:", error);
      throw error;
    }
  }

  // Get connection quality
  public getConnectionState(): ConnectionState | undefined {
    return this.agoraClient?.connectionState;
  }

  // Check if reconnecting
  public isCurrentlyReconnecting(): boolean {
    return this.isReconnecting;
  }

  // Force reconnection (to be called from components)
  public async forceReconnect(
    channelName: string,
    uid: number,
    role: "admin" | "user"
  ) {
    try {
      await this.leaveChannel();
      this.initializeAgoraClient(role);
      this.setupEventListeners();

      if (role === "admin") {
        await this.joinAsHost(channelName, uid, () => {
          console.log("Reconnected as host");
        });
      } else {
        await this.joinAsAudience(channelName, uid);
      }
    } catch (error) {
      console.error("Force reconnection failed:", error);
      throw error;
    }
  }
}
